import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Share2, MessageCircle, Copy, Image as ImageIcon, Send, Megaphone } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function BragCard({ rank }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o && !token) {
      setLoading(true);
      try {
        const { data } = await api.get(`/me/brag-link`, { params: { base: BACKEND } });
        setToken(data.token);
      } catch { toast.error("Couldn't create your brag card"); }
      finally { setLoading(false); }
    }
  };

  const shareUrl = token ? `${BACKEND}/api/share/rank/${token}` : "";
  const imgUrl = token ? `${BACKEND}/api/share/rank/${token}.png` : "";
  const caption = rank ? `I'm ranked #${rank} on GAFFER — think you can do better? ⚽` : "Check my GAFFER world ranking ⚽";

  const openWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(caption + " " + shareUrl)}`, "_blank");
  const openX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy"); }
  };
  const viewImage = () => window.open(imgUrl, "_blank");
  const nativeShare = async () => {
    try { await navigator.share({ title: "GAFFER", text: caption, url: shareUrl }); }
    catch { /* cancelled */ }
  };
  const canNative = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="brag-card-btn"
          className="bg-primary text-primary-foreground border-2 border-transparent hover:brightness-110">
          <Megaphone size={15} className="me-2" /> Brag
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-2 border-foreground rounded-none max-w-md" data-testid="brag-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase">Shout it from the terraces</DialogTitle>
          <DialogDescription className="font-data text-xs">Share your world ranking and dare your mates to beat it.</DialogDescription>
        </DialogHeader>

        <div className="border-2 border-border bg-background">
          {imgUrl
            ? <img src={imgUrl} alt="world ranking" className="w-full" data-testid="brag-preview-img" />
            : <div className="h-40 grid place-items-center text-muted-foreground font-data text-sm">{loading ? "…" : ""}</div>}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button onClick={openWhatsApp} disabled={!token} data-testid="brag-whatsapp-btn"
            className="bg-[hsl(145_63%_42%)] text-white border-2 border-transparent hover:brightness-110">
            <MessageCircle size={16} className="me-2" /> WhatsApp
          </Button>
          <Button onClick={openX} disabled={!token} data-testid="brag-x-btn"
            className="bg-foreground text-background border-2 border-transparent hover:brightness-110">
            Post to X
          </Button>
          <Button onClick={copyLink} disabled={!token} variant="outline" data-testid="brag-copy-btn">
            <Copy size={15} className="me-2" /> Copy link
          </Button>
          <Button onClick={viewImage} disabled={!token} variant="outline" data-testid="brag-image-btn">
            <ImageIcon size={15} className="me-2" /> View image
          </Button>
        </div>

        {canNative && (
          <Button onClick={nativeShare} disabled={!token} variant="ghost" data-testid="brag-native-btn" className="w-full">
            <Send size={15} className="me-2" /> Share…
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
